import { defineDocument } from './types.js'
import {
  OURS, SYNCED_ACTIONS, adjustmentsGrid, chargesGrid, currencySection, fcyColumns, invoiceStatusColumn,
  invoiceStatusFilter, ofParty, pricedLines, sentColumn, sentFilter, taxUnitChip, termsSection, withBillingUnit,
} from './common.js'

export const purchaseOrder = defineDocument({
  key: 'purchase_order',
  group: 'Purchase and Expense',
  blurb: 'Create new orders for materials, service requests or stock. You can also view existing orders.',
  route: 'pur_ords',
  label: 'Purchase order',
  labelPlural: 'Purchase orders',
  numbering: 'PURCHASE_ORDER',
  numberPrefix: '{CO}/PO#',
  workflow: 'PURCHASE_ORDER_3_STAGE',
  tabs: ['summary', 'related', 'revisions', 'form', 'drive'],

  /*
    The reference's "Purchase Order" dialog (PUR-025): the type, where the
    goods go, and the tax unit. The supplier is picked on the form, where it is
    required, with its contact and address beside it.
  */
  createWizard: {
    title: 'Purchase order',
    subtitle: 'Choose the type of order. You can change the rest on the form.',
    fields: [
      {
        key: 'orderType', label: 'Type', control: 'radio', required: true,
        lockAfterDraft: true,
        options: [
          { value: 'PROJECT', label: 'For project' },
          { value: 'STOCK', label: 'For stock' },
          { value: 'TOOL', label: 'For tool' },
        ],
      },
      // Asked only where it means something. A stock order has no project, and
      // a project order has no warehouse until you say the goods go to one.
      { key: 'projectId', label: 'Project', control: 'lookup', required: true, lookup: { entity: 'project' },
        visibleWhen: { field: 'orderType', equals: ['PROJECT'] } },
      { key: 'receiveAt', label: 'Receive materials at?', control: 'radio', required: true, defaultValue: 'SITE',
        visibleWhen: { field: 'orderType', equals: ['PROJECT'] },
        options: [{ value: 'SITE', label: 'Site' }, { value: 'WAREHOUSE', label: 'Warehouse' }] },
      { key: 'warehouseId', label: 'Warehouse', control: 'lookup', required: true, lookup: { entity: 'warehouse' },
        visibleWhen: { field: 'orderType', equals: ['STOCK', 'TOOL'] } },
      { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', required: true, lookup: { entity: 'tax_unit' } },
    ],
  },

  /*
    The reference's cards (PUR-026): General Info holds who the order is with
    and how they are reached; Billing and Delivery Info holds where it is
    billed and delivered, who buys and when it is due. The materials, the
    charges and the total follow, and the terms close the form.
  */
  formOrder: ['general', 'billing', 'currency', 'grid:ITEM', 'grid:CHARGE', 'grid:ADJUSTMENT', 'totalValue', 'terms'],
  form: {
    sections: [
      {
        key: 'general', n: '01', title: 'General info',
        fields: [
          { key: 'orderType', label: 'Type', control: 'select', required: true, width: 'half', lockAfterDraft: true,
            options: [{ value: 'PROJECT', label: 'For project' }, { value: 'STOCK', label: 'For stock' }, { value: 'TOOL', label: 'For tool' }] },
          // One order can buy for several jobs (PUR-027). The first is the
          // order's project — what scopes it and what its consignee starts on;
          // a line picked from another project's request says which job it is
          // for, and the items overview shows it.
          { key: 'projectIds', label: 'Projects', control: 'lookup', required: true, width: 'half',
            lookup: { entity: 'project', multiple: true },
            visibleWhen: { field: 'orderType', equals: ['PROJECT'] } },
          { key: 'supplierId', label: 'Supplier', control: 'lookup', required: true, width: 'half', lookup: { entity: 'supplier' },
            hint: 'A blocked supplier is refused when the order is submitted.' },
          { key: 'documentDate', label: 'Document date', control: 'date', required: true, width: 'half' },
          { key: 'supplierQuoteNo', label: 'Supplier quote no', control: 'text', width: 'half' },
          // Changeable after the wizard, as on the reference's form (PUR-036).
          { key: 'receiveAt', label: 'Receive materials at', control: 'select', required: true, width: 'half',
            visibleWhen: { field: 'orderType', equals: ['PROJECT'] },
            options: [{ value: 'SITE', label: 'Site' }, { value: 'WAREHOUSE', label: 'Warehouse' }] },
          { key: 'warehouseId', label: 'Warehouse', control: 'lookup', width: 'half', lookup: { entity: 'warehouse' },
            hint: 'Where the goods land. A receipt note against this order creates stock here.' },
          { key: 'notesToSupplier', label: 'Notes to supplier', control: 'textarea', width: 'full' },
          // The supplier's own, starting on its default (BILL-008, INV-024).
          { key: 'supplierContactId', label: 'Supplier contact', control: 'lookup', required: true, width: 'half', lookup: { entity: 'contact', scope: ofParty('supplierId') } },
          { key: 'supplierAddressId', label: 'Supplier address', control: 'lookup', required: true, width: 'half', lookup: { entity: 'address', scope: ofParty('supplierId') } },
          // Read off the supplier's address, or its record; kept as printed (PUR-036).
          { key: 'supplierGstin', label: 'Supplier GSTIN', control: 'text', width: 'half',
            hint: 'Taken from the supplier’s address, or its record. Change it only if the supplier has told you otherwise.' },
          { key: 'remarks', label: 'Remarks', control: 'textarea', width: 'full' },
          /*
            Ours, after the reference's own: the request, requisition,
            quotation or stock request the order answers.

            Both RFQ and quotation offered "Purchase order" as their next
            document and the order had no field pointing back at either, so
            awarding an RFQ and then buying at the awarded rate meant re-picking
            and re-typing every line — which is the payoff of the whole RFQ
            feature (docs/video-notes/06 §5).
          */
          // "Randomly you can't do PO for any item — only if you request"
          // (docs/video-notes/06 §8). Naming the request here is what lets
          // `Pull the open lines` resolve it, and what the
          // `order_without_request` control looks for.
          { key: 'materialRequestId', label: 'Against material request', control: 'lookup', width: 'half',
            lookup: { entity: 'material_request' },
            visibleWhen: { field: 'orderType', equals: ['PROJECT'] },
            hint: 'The site request this order answers. Its open lines can be pulled straight in.' },
          // docs/24 §3.4 — the purchase executive's decision the order carries out.
          { key: 'purchaseRequisitionId', label: 'Against purchase requisition', control: 'lookup', width: 'half',
            lookup: { entity: 'purchase_requisition' },
            hint: 'The requisition this order buys for. Its open lines can be pulled straight in.' },
          { key: 'supplierQuotationId', label: 'Against quotation', control: 'lookup', width: 'half',
            lookup: { entity: 'supplier_quotation' },
            hint: "The quotation this order is placed at. Pull its lines to buy at the rates quoted." },
          { key: 'stockPurchaseRequestId', label: 'Against stock request', control: 'lookup', width: 'half',
            lookup: { entity: 'stock_purchase_request' },
            visibleWhen: { field: 'orderType', equals: ['STOCK'] },
            hint: 'The warehouse request this order replenishes.' },
          { key: 'jobTypeId', label: 'Job type', control: 'lookup', width: 'half', lookup: { entity: 'job_type' } },
          { ...taxUnitChip(), width: 'half' },
        ],
      },
      {
        key: 'billing', n: '02', title: 'Billing and delivery info',
        fields: [
          // Our own addresses only, starting on the registered office (BILL-008, INV-024).
          { key: 'billingAddressId', label: 'Billing address', control: 'lookup', required: true, width: 'half', lookup: { entity: 'address', scope: OURS } },
          { key: 'deliveryAddressId', label: 'Delivery address', control: 'lookup', required: true, width: 'half', lookup: { entity: 'address', scope: OURS } },
          // docs/06 §4 — who the goods are consigned to, which is not always
          // the company buying them, and is what the transporter reads. It
          // starts on the project's client, or this company for stock and
          // tools (PUR-037).
          { key: 'consignee', label: 'Consignee', control: 'text', required: true, width: 'half',
            hint: 'Starts on the project’s client — or this company, for stock and tools.' },
          { key: 'purchaseExecutiveId', label: 'Purchase executive', control: 'lookup', required: true, width: 'half', lookup: { entity: 'employee' } },
          { key: 'deliveryDate', label: 'Delivery date', control: 'date', width: 'half',
            hint: 'Once this has passed with nothing received, the order joins the overdue work list.' },
          { key: 'paymentTerms', label: 'Payment terms', control: 'text', width: 'half' },
          { key: 'creditPeriodDays', label: 'Credit period (in days)', control: 'number', width: 'half' },
          // An employee, whose mobile becomes the delivery contact (PUR-037).
          { key: 'deliveryCoordinatorId', label: 'Delivery coordinator', control: 'lookup', width: 'half', lookup: { entity: 'employee' },
            hint: 'Their mobile is used as the delivery contact unless one is typed.' },
          { key: 'deliveryContactNo', label: 'Delivery contact no', control: 'text', width: 'half' },
          { key: 'interstate', label: 'Interstate (IGST)?', control: 'checkbox', width: 'half',
            hint: 'Set automatically from the supplier and tax unit states. Change it only if you are sure.' },
          { key: 'contactId', label: 'Site contact', control: 'lookup', width: 'half', lookup: { entity: 'contact', scope: OURS } },
        ],
      },
      currencySection(),
      termsSection(),
    ],
  },

  /*
    Built from the shared priced grid rather than written out by hand.

    Written out by hand it silently missed `isBillable` when that was added to
    every other grid, and the purchase order is the one step in the chain where
    the classification is invisible and cannot be corrected — a line typed in
    or imported from Excel defaulted to billable with nobody able to see it.
  */
  lines: (() => {
    // Ordered in one unit, billed in another (PUR-048).
    const grid = withBillingUnit(pricedLines('Material info', '03'))
    return {
      ...grid,
      // "Items : 3 material(s) added" on the Summary (PUR-035).
      countAs: { label: 'Items', noun: 'material(s)' },
      columns: [
        ...grid.columns,
        // The job a line is bought for, on an order serving several (PUR-027).
        // Carried from the request it was picked from; left empty, the order's own.
        { key: 'forProjectId', label: 'For project', type: 'text' as const, width: 180, editable: true, dialogOnly: true,
          lookup: { entity: 'project' },
          hint: 'The job this line is bought for, when it is not the order’s first project.' },
      ],
      /*
        A project order buys what the site asked for (PUR-029): "Select the
        materials from the available material/spare requests", with no
        estimate, spreadsheet or hand-typed line. Stock and tool orders keep
        every way in.
      */
      sources: [
        { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' as const,
          visibleWhen: { field: 'orderType', equals: ['STOCK', 'TOOL'] } },
        { key: 'request', label: 'Pick from material requests', source: 'request' as const },
        { key: 'requisition', label: 'Pick from purchase requisitions', source: 'requisition' as const },
        { key: 'excel', label: 'Import from Excel', source: 'excel' as const,
          visibleWhen: { field: 'orderType', equals: ['STOCK', 'TOOL'] } },
      ],
      pickOnly: {
        when: { field: 'orderType', equals: ['PROJECT'] },
        helper: 'Select the materials from the available material/spare requests.',
      },
    }
  })(),

  grids: [chargesGrid(), adjustmentsGrid()],

  // Supplier first, as the reference lists orders (PUR-032).
  columns: [
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Document date', type: 'date', width: 128 },
    // For project, for stock, for tool (PUR-032).
    { key: 'orderType', label: 'Type', type: 'text', width: 130 },
    // Every project the order serves (PUR-027).
    { key: 'projectNames', label: 'Projects', type: 'text' },
    { key: 'jobTypeName', label: 'Job types', type: 'text', width: 205 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
    { key: 'total', label: 'Total', type: 'money', width: 156, align: 'right' },
    sentColumn(true),
    invoiceStatusColumn(),
    ...fcyColumns(),
  ],

  filters: [
    { key: 'documentNo', label: 'Document number', control: 'text' },
    { key: 'documentDate', label: 'Document date', control: 'dateRange' },
    { key: 'documentStatus', label: 'Status', control: 'status' },
    { key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } },
    { key: 'orderType', label: 'Type', control: 'select', options: [
      { value: 'PROJECT', label: 'For project' }, { value: 'STOCK', label: 'For stock' }, { value: 'TOOL', label: 'For tool' },
    ] },
    { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'submittedById', label: 'Submitted by', control: 'lookup', lookup: { entity: 'employee' } },
    { key: 'fulfilment', label: 'Progress', control: 'select', options: [
      { value: 'OPEN', label: 'Anything still open' },
      { value: 'PARTIAL', label: 'Partly received' },
      { value: 'COMPLETE', label: 'Fully received' },
    ] },
    sentFilter(),
    invoiceStatusFilter(),
  ],

  actions: [...SYNCED_ACTIONS, 'preClose', 'undoPreClose'],
  rules: [
    'order_without_request', 'order_without_requisition', 'non_tendered_order', 'rate_exceeds_estimation',
    'excess_purchase_quantity', 'brand_mismatch', 'purchase_plan_required',
    'quotation_not_awarded', 'supplier_not_approved',
  ],
  allocatesFrom: ['material_request', 'purchase_requisition', 'supplier_quotation', 'stock_purchase_request'],
  createsNext: [
    { docType: 'goods_receipt', label: 'Receipt note (GRN)', whenStatus: 'APPROVED' },
    { docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' },
  ],
} as const)
